import { addMonths, parseISO, startOfDay, startOfMonth, subMonths } from 'date-fns';
import React, { useState } from 'react';

import { Icon, IconCalendar, IconClose } from '@flixbus/honeycomb-icons-react';
import { Button, Calendar, Input } from '@flixbus/honeycomb-react';

import { useOutsideClick } from 'hooks/useClickOutside';
import { CALENDAR_MONTHS, CALENDAR_WEEKDAYS } from 'utils/calendarConfig';
import { formatDateDisplay } from 'utils/datetime';

import styles from './styles.module.css';

const PAST_MONTHS = 3;
const FUTURE_MONTHS = 3;

const toDateObject = (value) => {
	if (!value) return null;

	return value instanceof Date ? value : parseISO(value);
};

const getDefaultMonth = (from, today) => startOfMonth(toDateObject(from) ?? today);

const DaterangeFilter = ({
	fieldControl,
	onReset,
	label,
	onSubmitOnChange,
	doSubmitOnChange = false,
}) => {
	const [showCalendar, setShowCalendar] = useState(false);
	const ref = useOutsideClick(() => setShowCalendar(false));

	const { value: fieldValue, disabled, onChange } = fieldControl;
	const selectedFrom = fieldValue?.from ?? null;

	const today = startOfDay(new Date());
	const startDate = subMonths(today, PAST_MONTHS);
	const endDate = addMonths(today, FUTURE_MONTHS);
	const defaultMonth = getDefaultMonth(selectedFrom, today);
	const selected = toDateObject(selectedFrom);

	const handleSelect = (date) => {
		if (disabled) return;

		onChange({ from: date });

		if (doSubmitOnChange) onSubmitOnChange();

		setShowCalendar(false);
	};

	const activateCalendar = () => {
		if (disabled) return;
		setShowCalendar(true);
	};

	const inputProps = {
		disabled,
		readOnly: true,
		label,
		'aria-label': label ?? 'Select date',
		placeholder: 'dd.mm.yyyy',
		id: 'departure-range-input',
		value: '',
		iconLeft: (
			<Button aria-label="Open date picker" onClick={activateCalendar}>
				<Icon id="alignIcon" InlineIcon={IconCalendar} />
			</Button>
		),
		onFocus: activateCalendar,
	};

	if (selectedFrom) {
		inputProps.iconRight = (
			<Button onClick={onReset}>
				<Icon id="alignIcon" InlineIcon={IconClose} />
			</Button>
		);

		inputProps.value = formatDateDisplay(selectedFrom, { style: 'dotted' });
	}

	return (
		<div className={styles.holder} ref={ref}>
			<Input {...inputProps} />
			{showCalendar && (
				<div className={styles.calendar} data-testid="calendar">
					<Calendar
						aria-label="Select calendar"
						id="simple-picker"
						appearance="compact"
						selected={selected ?? today}
						startDate={startDate}
						endDate={endDate}
						defaultMonth={defaultMonth}
						days={CALENDAR_WEEKDAYS}
						months={CALENDAR_MONTHS}
						handleSelect={handleSelect}
						showCurrentDate
					/>
				</div>
			)}
		</div>
	);
};

export default DaterangeFilter;
